// Partes e dados do Termo de Negociação e Confissão de Dívida (modelo único).
//
// Até set/2026 o parcelamento gerava dois termos diferentes no n8n (só dívida,
// e dívida + saldo de energia) e mandava ao fluxo só o nome e o e-mail do
// cliente: os termos saíam SEM CPF, sem endereço e com "Fatura 8146" no lugar
// do mês. O modelo único (contexto/vinicius/termo-padrao-negociacao-confissao-
// divida-editavel.docx) trata os dois casos, e mais dois que antes não existiam:
//
//   - Responsável que assume a dívida (ex.: comprou o estabelecimento). O
//     titular continua DEVEDOR e o responsável entra como COOBRIGADO solidário
//     no Anexo D — é o desenho do próprio termo (Cláusulas 1.3 e 7.1): a
//     entrada de um terceiro não libera o devedor original.
//   - Devedor pessoa jurídica: representante legal e, se ele aceitar, a
//     coobrigação dele como pessoa física (Anexo E).
//
// Sem I/O: roda sob `node --test`. Quem busca cadastro é parcelamentoAcordo.ts.

import { faltasEnderecoPagador, type EnderecoPagador } from '../../supabase/functions/_shared/pagadorAcordo.ts';
import { mesReferenciaValido } from './duplicataFatura.ts';
import { mesReferenciaDe } from './parcelamentoCalculo.ts';
import type { OrigemDesconto } from './parcelamentoRegras.ts';

export { pagadorDoAcordo, camposFaturaDoPagador, faltasEnderecoPagador } from '../../supabase/functions/_shared/pagadorAcordo.ts';
export type { EnderecoPagador, PagadorAcordo } from '../../supabase/functions/_shared/pagadorAcordo.ts';

export interface ParteTermo {
  nome: string;
  documento: string;
  endereco: string;
  email: string;
  whatsapp: string;
}

export interface RepresentanteTermo {
  nome: string;
  cpf: string;
  /** Documento que comprova os poderes. Padrão: contrato social. */
  poderes: string;
}

export interface DevedorTermo extends ParteTermo {
  /** Só pessoa jurídica. */
  representante: RepresentanteTermo | null;
  /** Anexo E: o representante assina também em nome próprio, como coobrigado. */
  coobrigacaoPf: { endereco: string } | null;
}

export interface CoobrigadoTermo extends ParteTermo {
  /** Por que ele assume a dívida — vai escrito no Anexo D. */
  motivo: string;
  /** Endereço em campos: o banco exige CEP, rua, número, bairro, cidade e UF para
   *  registrar boleto no nome dele. `endereco` (texto do termo) sai destes. */
  enderecoPartes: EnderecoPagador;
}

/** Anexo B. Cláusula 4.4 do termo: sem estes dados o saldo NÃO entra no acordo. */
export interface AnexoBTermo {
  evento: EventoQuebra;
  eventoDescricao: string;
  artigos: string;
  dataEvento: string;
  documentoEvento: string;
  razao: string;
  origemValidade: string;
  faturaFonte: string;
  /** Mês a que o saldo pertence ("SET/2026"), informado por quem faz o acordo
   *  (chamado #6185). Sai em "Origem e validade do saldo" do Anexo B. Ausente =
   *  acordo gravado antes do campo, que continua podendo enviar o termo. */
  mesSaldo?: string;
  tributos: string;
  termoAdesao: string;
}

export interface FaturaTermo {
  id: string | number;
  uc: string;
  competencia: string;
  documento: string;
}

export interface DadosTermo {
  versao: 1;
  devedor: DevedorTermo;
  coobrigado: CoobrigadoTermo | null;
  anexoB: AnexoBTermo | null;
  faturas: FaturaTermo[];
  /** Boletos do acordo (entrada e parcelas) no nome do coobrigado, não do titular.
   *  Opcional: acordos gravados antes da opção não têm o campo. */
  boletoNoResponsavel?: boolean;
  /** De onde veio o desconto na dívida (chamado #6174). Mora aqui, como o
   *  Anexo B, porque muda o texto do termo: valor já pago sai do confessado.
   *  O valor em si vem da memória de cálculo. Null/ausente = sem desconto ou
   *  acordo anterior ao #6174. */
  desconto?: { origem: OrigemDesconto; descricao: string } | null;
}

// ----------------------------------------------------------------- formatos

export const soDigitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');

export function tipoPessoa(documento: unknown): 'PF' | 'PJ' | null {
  const d = soDigitos(documento);
  if (d.length === 11) return 'PF';
  if (d.length === 14) return 'PJ';
  return null;
}

export function formatarDocumento(documento: unknown): string {
  const d = soDigitos(documento);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return String(documento ?? '').trim();
}

// Sem "+" antes do @: o Autentique recusa ("format_is_invalid") e o termo morria
// no n8n depois de copiar o modelo no Drive (teste de 24/09/2026 com
// "nome+responsavel@gmail.com"). Barrar aqui dá o motivo na hora, na tela.
export const emailValido = (v: unknown) => /^[^\s@+]+@[^\s@]+\.[^\s@]+$/.test(String(v ?? '').trim());

const primeiro = (...vs: unknown[]) => {
  for (const v of vs) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
};

/** "Rua X, 123, Centro, Morrinhos/GO, CEP 75650-000" — pula o que faltar. */
export function montarEndereco(p: {
  logradouro?: unknown; numero?: unknown; bairro?: unknown; cidade?: unknown; uf?: unknown; cep?: unknown;
}): string {
  const cidade = [p.cidade, p.uf].map((v) => String(v ?? '').trim()).filter(Boolean).join('/');
  const cep = soDigitos(p.cep);
  return [
    String(p.logradouro ?? '').trim(),
    String(p.numero ?? '').trim(),
    String(p.bairro ?? '').trim(),
    cidade,
    cep.length === 8 ? `CEP ${cep.slice(0, 5)}-${cep.slice(5)}` : '',
  ].filter(Boolean).join(', ');
}

// ----------------------------------------------------------------- padrões

export interface CadastroNegociacao {
  cliente_nome?: string | null;
  cpf_cnpj?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  nome_representante_legal?: string | null;
  cpf_representante?: string | null;
  data_assinatura_termo?: string | null;
}

export interface CadastroUc {
  nome_cliente?: string | null;
  cpf_cnpj_cliente?: string | null;
  email_cliente?: string | null;
  email_cliente_2?: string | null;
  whatsapp_cliente?: string | null;
  whatsapp_cliente_2?: string | null;
  endereco?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  cep?: string | null;
}

export interface FaturaEndereco {
  nome_cliente_boleto?: string | null;
  cliente_nome?: string | null;
  cpf_cnpj_cliente_boleto?: string | null;
  cliente_cpf_cnpj?: string | null;
  end_logradouro?: string | null;
  end_numero?: string | null;
  end_bairro?: string | null;
  end_cidade?: string | null;
  end_uf?: string | null;
  end_cep?: string | null;
}

/**
 * Devedor pré-preenchido a partir do cadastro de HOJE.
 *
 * Nome e documento: assinante do termo de adesão (negociação) → cadastro da UC
 * → fatura. A fatura fica por último porque guarda o nome do dia em que o
 * boleto saiu, que pode ser o titular da conta de luz (ver CriarParcelamentoModal).
 *
 * Contato: cadastro da UC primeiro — é o e-mail e o WhatsApp que a cobrança já
 * usa todo mês —, depois a negociação. Antes a tela deixava os dois campos em
 * branco e o analista tinha de ir procurar.
 */
export function devedorPadrao(fontes: {
  negociacao?: CadastroNegociacao | null;
  uc?: CadastroUc | null;
  fatura?: FaturaEndereco | null;
}): DevedorTermo {
  const { negociacao: n, uc, fatura: f } = fontes;
  const nome = primeiro(n?.cliente_nome, uc?.nome_cliente, f?.nome_cliente_boleto, f?.cliente_nome);
  const documento = soDigitos(primeiro(n?.cpf_cnpj, uc?.cpf_cnpj_cliente, f?.cpf_cnpj_cliente_boleto, f?.cliente_cpf_cnpj));
  const endereco = primeiro(
    uc ? montarEndereco(uc) : '',
    uc?.endereco,
    f ? montarEndereco({
      logradouro: f.end_logradouro, numero: f.end_numero, bairro: f.end_bairro,
      cidade: f.end_cidade, uf: f.end_uf, cep: f.end_cep,
    }) : '',
  );
  const pj = tipoPessoa(documento) === 'PJ';
  return {
    nome,
    documento,
    endereco,
    email: primeiro(uc?.email_cliente, n?.email, uc?.email_cliente_2),
    whatsapp: primeiro(uc?.whatsapp_cliente, n?.whatsapp, uc?.whatsapp_cliente_2),
    representante: pj
      ? { nome: primeiro(n?.nome_representante_legal), cpf: soDigitos(n?.cpf_representante), poderes: 'contrato social' }
      : null,
    coobrigacaoPf: null,
  };
}

export const enderecoVazio = (): EnderecoPagador => ({ cep: '', logradouro: '', numero: '', bairro: '', cidade: '', uf: '' });

export const coobrigadoVazio = (): CoobrigadoTermo => ({
  nome: '', documento: '', endereco: '', email: '', whatsapp: '',
  motivo: 'aquisição do estabelecimento atendido pela unidade consumidora',
  enderecoPartes: enderecoVazio(),
});

/** Texto do endereço (o que vai no termo) a partir dos campos. */
export const enderecoTexto = (e: EnderecoPagador) =>
  montarEndereco({ logradouro: e.logradouro, numero: e.numero, bairro: e.bairro, cidade: e.cidade, uf: e.uf, cep: e.cep });

export type EventoQuebra = 'cancelamento_inadimplencia' | 'rescisao' | 'troca_titularidade' | 'outro';

/** Eventos da Cláusula 4.1 e os artigos do Termo de Adesão (rev. V) que os tratam:
 *  Art. 11 inadimplência e saldo; Art. 13 rescisão; Art. 14 créditos remanescentes. */
export const EVENTOS_QUEBRA: Record<EventoQuebra, { rotulo: string; artigos: string; razao: string }> = {
  cancelamento_inadimplencia: {
    rotulo: 'Cancelamento por inadimplência',
    artigos: 'Art. 11 e Art. 14',
    razao: 'Com o cancelamento por inadimplência, a UC deixou de receber compensação da VIGOR e o saldo acumulado não pode mais ser utilizado nem estornado ao arranjo.',
  },
  rescisao: {
    rotulo: 'Rescisão pelo associado',
    artigos: 'Art. 13 e Art. 14',
    razao: 'Com a rescisão, a UC deixou o arranjo da VIGOR e o saldo acumulado não pode mais ser utilizado nem estornado ao arranjo.',
  },
  troca_titularidade: {
    rotulo: 'Troca de titularidade sem comunicação prévia à VIGOR',
    artigos: 'Art. 11 e Art. 14',
    razao: 'A troca de titularidade da UC sem comunicação prévia impediu a VIGOR de transferir ou aproveitar o saldo acumulado.',
  },
  outro: { rotulo: 'Outro descumprimento contratual', artigos: '', razao: '' },
};

/** Texto de "Origem e validade do saldo" no Anexo B. O mês do saldo vem na
 *  frente: é o que o cliente procura ao conferir de onde saiu a cobrança. */
export function origemValidadeSaldo(p: { mesSaldo?: string | null; faturaFonte?: string | null }): string {
  const mes = String(p.mesSaldo ?? '').trim().toUpperCase();
  const fonte = String(p.faturaFonte ?? '').trim();
  return `${mes ? `Saldo referente a ${mes}. ` : ''}Créditos de energia injetados pela VIGOR na UC e informados na fatura da distribuidora${fonte ? ` de ${fonte}` : ''}; validade de 60 meses contados da geração (Lei 14.300/2022).`;
}

/** "SET/2026" → "2026-09" (valor do `<input type="month">`). Vazio se inválido. */
export function mesSaldoParaInput(mes: string | null | undefined): string {
  const v = String(mes ?? '').trim().toUpperCase();
  if (!mesReferenciaValido(v)) return '';
  const [abrev, ano] = v.split('/');
  const n = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'].indexOf(abrev) + 1;
  return `${ano}-${String(n).padStart(2, '0')}`;
}

/** "2026-09" (valor do `<input type="month">`) → "SET/2026". */
export function mesSaldoDoInput(valor: string): string {
  return /^\d{4}-\d{2}$/.test(valor) ? mesReferenciaDe(`${valor}-01`) : '';
}

/**
 * `mesSaldo` só entra quando é passado: o acordo novo nasce com ele vazio, para
 * ser preenchido na tela; o termo de acordo antigo é remontado sem ele.
 */
export function anexoBPadrao(p: {
  faturaFonte?: string | null;
  dataAssinaturaTermo?: string | null;
  mesSaldo?: string;
}): AnexoBTermo {
  const e = EVENTOS_QUEBRA.cancelamento_inadimplencia;
  const fonte = String(p.faturaFonte ?? '').trim();
  const assinado = String(p.dataAssinaturaTermo ?? '').slice(0, 10);
  const [a, m, d] = assinado.split('-');
  return {
    evento: 'cancelamento_inadimplencia',
    eventoDescricao: e.rotulo,
    artigos: e.artigos,
    dataEvento: '',
    documentoEvento: '',
    razao: e.razao,
    origemValidade: origemValidadeSaldo({ mesSaldo: p.mesSaldo, faturaFonte: fonte }),
    faturaFonte: fonte,
    ...(p.mesSaldo !== undefined ? { mesSaldo: p.mesSaldo } : {}),
    tributos: 'ICMS, PIS e COFINS incluídos na tarifa (tarifa com tributos da fatura-fonte).',
    termoAdesao: assinado && d ? `Termo de Adesão assinado em ${d}/${m}/${a}` : 'Termo de Adesão vinculado à UC',
  };
}

/** Troca o evento e reescreve artigos e razão — só se o operador não os editou. */
export function trocarEvento(atual: AnexoBTermo, evento: EventoQuebra): AnexoBTermo {
  const antes = EVENTOS_QUEBRA[atual.evento];
  const depois = EVENTOS_QUEBRA[evento];
  return {
    ...atual,
    evento,
    eventoDescricao: evento === 'outro' ? (atual.evento === 'outro' ? atual.eventoDescricao : '') : depois.rotulo,
    artigos: atual.artigos === antes.artigos ? depois.artigos : atual.artigos,
    razao: atual.razao === antes.razao ? depois.razao : atual.razao,
  };
}

// ----------------------------------------------------------------- validação

/**
 * O que falta para o termo poder ser gerado. Lista vazia = pode.
 *
 * `exigirContato` liga quando o termo vai ser ENVIADO agora: sem e-mail o
 * Autentique não entrega o convite. Criar a proposta sem enviar só exige a
 * qualificação das partes.
 */
export function validarDadosTermo(
  dados: Pick<DadosTermo, 'devedor' | 'coobrigado' | 'anexoB' | 'boletoNoResponsavel'>,
  opcoes: { exigirContato: boolean; temSaldo: boolean },
): string[] {
  const erros: string[] = [];
  const { devedor: dv, coobrigado: co, anexoB } = dados;

  if (!dv.nome.trim()) erros.push('Informe o nome do devedor (titular).');
  const tipo = tipoPessoa(dv.documento);
  if (!tipo) erros.push('O CPF/CNPJ do devedor precisa ter 11 ou 14 dígitos.');
  if (!dv.endereco.trim()) erros.push('Informe o endereço do devedor.');
  if (opcoes.exigirContato && !emailValido(dv.email)) {
    erros.push('Informe um e-mail válido do devedor (sem "+", que o Autentique recusa) — é por ele que o termo chega para assinatura.');
  }
  if (tipo === 'PJ') {
    if (!dv.representante?.nome.trim()) erros.push('Devedor é empresa: informe o representante legal que assina por ela.');
    if (soDigitos(dv.representante?.cpf).length !== 11) erros.push('Informe o CPF (11 dígitos) do representante legal.');
    if (dv.coobrigacaoPf && !dv.coobrigacaoPf.endereco.trim()) {
      erros.push('Anexo E: informe o endereço do representante, que assina também em nome próprio.');
    }
  }

  if (co) {
    if (!co.nome.trim()) erros.push('Informe o nome do responsável que assume a dívida.');
    if (!tipoPessoa(co.documento)) erros.push('O CPF/CNPJ do responsável precisa ter 11 ou 14 dígitos.');
    else if (soDigitos(co.documento) === soDigitos(dv.documento)) {
      erros.push('O responsável tem o mesmo CPF/CNPJ do titular — se é a mesma pessoa, desmarque "outra pessoa assume a dívida".');
    }
    if (dados.boletoNoResponsavel) {
      // O banco não registra boleto sem o endereço completo do pagador.
      const faltas = faltasEnderecoPagador(co.enderecoPartes);
      if (faltas.length) erros.push(`Boleto no nome do responsável: falta ${faltas.join(', ')} do endereço dele.`);
    } else if (!co.endereco.trim()) {
      erros.push('Informe o endereço do responsável que assume a dívida.');
    }
    if (opcoes.exigirContato) {
      if (!emailValido(co.email)) erros.push('Informe um e-mail válido do responsável (sem "+", que o Autentique recusa) — ele também assina o termo.');
      else if (co.email.trim().toLowerCase() === dv.email.trim().toLowerCase()) {
        erros.push('Titular e responsável precisam de e-mails diferentes: cada um recebe o próprio convite de assinatura.');
      }
    }
  }

  if (opcoes.temSaldo) {
    if (!anexoB) erros.push('Preencha os dados do Anexo B (saldo de energia).');
    else {
      if (anexoB.evento === 'outro' && !anexoB.eventoDescricao.trim()) erros.push('Anexo B: descreva o evento de quebra.');
      if (!anexoB.artigos.trim()) erros.push('Anexo B: informe os artigos do Termo de Adesão aplicáveis.');
      if (!/^\d{4}-\d{2}-\d{2}$/.test(anexoB.dataEvento)) erros.push('Anexo B: informe a data do evento de quebra.');
      if (!anexoB.documentoEvento.trim()) erros.push('Anexo B: informe o documento que comprova o evento.');
      if (!anexoB.razao.trim()) erros.push('Anexo B: explique por que o saldo não pode ser usado nem estornado.');
      // Acordo gravado antes do #6185 não tem o campo e segue podendo enviar o termo.
      if (anexoB.mesSaldo !== undefined && !mesReferenciaValido(anexoB.mesSaldo)) {
        erros.push('Anexo B: informe o mês a que o saldo pertence.');
      }
    }
  }
  return erros;
}

// ----------------------------------------------------------------- payload

export interface AcordoParaTermo {
  id: string;
  tipo_contrato: 'divida' | 'divida_saldo' | string;
  uc_codigo_snapshot?: string | null;
  created_at?: string | null;
  memoria_calculo: Record<string, unknown> | null;
  multa_percent?: number | null;
  mora_mensal_percent?: number | null;
  saldo_kwh?: number | null;
  saldo_tarifa?: number | null;
  saldo_desconto_percent?: number | null;
}

export interface SignatarioInterno {
  nome: string;
  cpf?: string;
  email: string;
}

/**
 * Corpo do webhook do n8n (versão 2 do contrato).
 *
 * Carrega também os campos da versão 1 (`cliente_nome`, `email_cliente`...):
 * o fluxo decide o ramo por `versao_payload`, e manter os nomes antigos deixa a
 * trilha de execuções do n8n legível para quem só conhece o contrato anterior.
 */
export function montarPayloadTermo(p: {
  acordo: AcordoParaTermo;
  dados: DadosTermo;
  signatarios: { procurador: SignatarioInterno | null; testemunha1: SignatarioInterno | null; testemunha2: SignatarioInterno | null };
  sandbox: boolean;
}) {
  const { acordo, dados, signatarios } = p;
  const dv = dados.devedor;
  const pj = tipoPessoa(dv.documento) === 'PJ';
  const comSaldo = acordo.tipo_contrato === 'divida_saldo';
  return {
    versao_payload: 2,
    parcelamento_id: acordo.id,
    sandbox: p.sandbox,
    tipo_contrato: acordo.tipo_contrato,
    criado_em: String(acordo.created_at || '').slice(0, 10) || null,
    uc_codigo: acordo.uc_codigo_snapshot || '',
    memoria_calculo: acordo.memoria_calculo,
    multa_percent: Number(acordo.multa_percent) || 0,
    mora_mensal_percent: Number(acordo.mora_mensal_percent) || 0,
    saldo: comSaldo
      ? {
        kwh: Number(acordo.saldo_kwh) || 0,
        tarifa: Number(acordo.saldo_tarifa) || 0,
        desconto_percent: Number(acordo.saldo_desconto_percent) || 0,
      }
      : null,
    devedor: {
      nome: dv.nome.trim(),
      documento: soDigitos(dv.documento),
      tipo: pj ? 'PJ' : 'PF',
      endereco: dv.endereco.trim(),
      email: dv.email.trim(),
      whatsapp: dv.whatsapp.trim(),
      representante: pj && dv.representante
        ? { nome: dv.representante.nome.trim(), cpf: soDigitos(dv.representante.cpf), poderes: dv.representante.poderes.trim() || 'contrato social' }
        : null,
      coobrigacao_pf: pj && dv.representante && dv.coobrigacaoPf
        ? { endereco: dv.coobrigacaoPf.endereco.trim() }
        : null,
    },
    coobrigado: dados.coobrigado
      ? {
        nome: dados.coobrigado.nome.trim(),
        documento: soDigitos(dados.coobrigado.documento),
        endereco: dados.coobrigado.endereco.trim(),
        email: dados.coobrigado.email.trim(),
        whatsapp: dados.coobrigado.whatsapp.trim(),
        motivo: dados.coobrigado.motivo.trim(),
      }
      : null,
    anexo_b: comSaldo ? dados.anexoB : null,
    desconto: dados.desconto
      ? { origem: dados.desconto.origem, descricao: dados.desconto.descricao.trim() }
      : null,
    faturas: dados.faturas.map((f) => ({
      id: f.id, uc: f.uc, competencia: f.competencia, documento: f.documento,
    })),
    procurador: signatarios.procurador,
    testemunha1: signatarios.testemunha1,
    testemunha2: signatarios.testemunha2,
    // contrato v1 — ver comentário da função
    cliente_nome: dv.nome.trim(),
    cpf_cnpj: soDigitos(dv.documento),
    email_cliente: dv.email.trim(),
    whatsapp_cliente: dv.whatsapp.trim(),
  };
}

/** Rótulo da fatura no Anexo A: o boleto quando existe (é o número que o
 *  cliente tem na mão), senão o id interno. */
export function faturaParaTermo(f: {
  id: string | number;
  uc_codigo?: string | null;
  mes_referencia?: string | null;
  num_cobranca_boleto?: string | null;
}): FaturaTermo {
  return {
    id: f.id,
    uc: String(f.uc_codigo ?? '').trim(),
    competencia: String(f.mes_referencia ?? '').trim(),
    documento: f.num_cobranca_boleto ? `Boleto ${f.num_cobranca_boleto}` : `Fatura ${f.id}`,
  };
}
